// Format dates for input fields (YYYY-MM-DD) – API and <input type="date"> only
export function formatDateForInput(dateString?: string | null): string {
  if (!dateString) {
    return "";
  }
  try {
    const date = new Date(dateString);
    const year: number = date.getUTCFullYear();
    const month: string = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day: string = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
}

/**
 * Parse DD/MM/YYYY or DD-MM-YYYY to YYYY-MM-DD (ISO). Returns null if invalid.
 * Use when user types in display format and we need to store/send ISO.
 */
export function parseDateFromDisplay(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const match = /^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/.exec(trimmed);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  if (!day || !month || !year) return null;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  const monthStr = String(month).padStart(2, "0");
  const dayStr = String(day).padStart(2, "0");
  return `${year}-${monthStr}-${dayStr}`;
}

/** Today's date in YYYY-MM-DD (local timezone). Use for date input max to block future dates. */
export function getTodayYYYYMMDD(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Display date only: dd/mm/yyyy. Use for dates without time (e.g. due date, birth date). */
export function formatDate(dateString: string | Date | null | undefined): string {
  if (dateString == null || dateString === "") {
    return "N/A";
  }
  try {
    const date = typeof dateString === "string" ? new Date(dateString) : dateString;
    if (Number.isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return "N/A";
  }
}

/** Display date and time: dd/mm/yyyy, hh:mm (24-hour). Use for timestamps in local timezone. */
export function formatDateTime(dateString: string | Date | null | undefined): string {
  if (dateString == null || dateString === "") {
    return "N/A";
  }
  try {
    const date = typeof dateString === "string" ? new Date(dateString) : dateString;
    if (Number.isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  } catch {
    return "N/A";
  }
}

/** Display date and time in UTC: dd/mm/yyyy, hh:mm UTC. Use for created_at / updated_at from DB. */
export { formatDateTimeUTC } from 'evoly-common/date-time-display'

export function formatTime(seconds: number): string {
  const mins: number = Math.floor(seconds / 60);
  const secs: number = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
